import { useEffect, useRef, useMemo, useCallback } from 'react';
import useSWR from 'swr';
import { fetchWithAuth, fetcherWithAuth } from '../utils/fetchWithAuth';

export const useDatasets = (selectedDataset) => {
    const columnValuesCache = useRef({});

    // Fetch available datasets
    const {
        data: datasetsData,
        error: datasetsError,
        isLoading: loadingDatasets,
    } = useSWR('/wt/fenix/graphs/metadata/', fetcherWithAuth, {
        revalidateOnFocus: false,
    });

    const datasets = useMemo(() => {
        if (!datasetsData) return [];
        const list =
            datasetsData.items ||
            datasetsData.datasets ||
            (Array.isArray(datasetsData) ? datasetsData : []);
        return [...list].sort();
    }, [datasetsData]);

    // Fetch fields for selected dataset
    const fieldsUrl = selectedDataset
        ? `/wt/fenix/graphs/metadata/${encodeURIComponent(selectedDataset)}/`
        : null;

    const {
        data: fieldsData,
        error: fieldsError,
        isLoading: loadingFields,
    } = useSWR(fieldsUrl, fetcherWithAuth, {
        revalidateOnFocus: false,
    });

    const datasetFields = useMemo(() => {
        if (!fieldsData) return [];
        const rawFields =
            fieldsData.fields ||
            fieldsData.items ||
            fieldsData.columns ||
            (Array.isArray(fieldsData) ? fieldsData : []);
        const fieldList = rawFields.map((f) =>
            typeof f === 'object' ? f.name : f
        );
        return [...fieldList].sort();
    }, [fieldsData]);

    const filterableFields = useMemo(() => {
        if (!fieldsData) return [];
        const rawFilterable = fieldsData.filterable || [];
        const filterableList = rawFilterable.map((f) => ({
            id: typeof f === 'object' ? f.name : f,
            type: typeof f === 'object' ? f.type : 'string',
        }));
        return [...filterableList].sort((a, b) => a.id.localeCompare(b.id));
    }, [fieldsData]);

    // Clear column cache when dataset changes
    useEffect(() => {
        columnValuesCache.current = {};
    }, [selectedDataset]);

    // Fetch unique values for a specific column
    const fetchColumnValues = useCallback(async (dataset, column) => {
        if (!dataset || !column) return [];

        const cacheKey = `${dataset}:${column}`;
        if (columnValuesCache.current[cacheKey]) {
            return columnValuesCache.current[cacheKey];
        }

        try {
            const response = await fetchWithAuth(
                `/wt/fenix/graphs/metadata/${encodeURIComponent(dataset)}/${encodeURIComponent(column)}/`
            );
            if (!response || !response.ok) return [];

            const data = await response.json();
            const values =
                data.items || data.values || data.unique_values || [];

            columnValuesCache.current[cacheKey] = values;
            return values;
        } catch (err) {
            console.error('Error fetching column values:', err);
            return [];
        }
    }, []);

    return {
        datasets,
        datasetFields,
        filterableFields,
        loadingDatasets,
        loadingFields,
        fetchColumnValues,
        datasetsError: datasetsError?.message || null,
        fieldsError: fieldsError?.message || null,
    };
};
